import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AuthorityFilter,
    CombinedMissionStatus,
    DO_NOTHING,
    DeepPartial,
    FEATURE_FLIGHT_CONFORMANCE,
    FEATURE_LOTAM,
    FEATURE_PROJECTS,
    FlightConformanceResultDto,
    FlightLogFileDto,
    FlightLogsService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    GqlMissionCriteria,
    LocationDto,
    LocationSummary,
    MISSION_TYPES,
    MissionService,
    MissionSortField,
    MissionSortFieldName,
    MissionSortFields,
    MissionSummary,
    MissionSummaryDto,
    OrganisationAuthorityService,
    PersonsOrganisationDto,
    PreferencesService,
    ProjectDto,
    ProjectsService,
    SimpleAuthorityDto,
    StateUpdateCommand,
    WorkTracker,
    defaultTimezone,
    hasFeatureFlag,
    isApprovalApplicable,
    observeFormControl,
    patchState,
    patchableState,
    setState,
    validatedSortEntry
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableAction,
    TableColumn,
    TableConfig,
    TableSearchValues,
    TableSetupUserPreferences
} from '@flyfreely-portal-ui/flyfreely-table';
import { MissionNoticesDataService } from '@flyfreely-portal-ui/notices-shared';
import { OperationsSearchDataService } from '@flyfreely-portal-ui/operations-shared';
import { SitesDataService } from '@flyfreely-portal-ui/sites-shared';
import {
    FormatAuthorityPipe,
    FormatColumnArrayPipe,
    FormatDateTimePipe,
    FormatMissionStatusPipe,
    FormatPersonPipe,
    findMostPressingConformance,
    flightConformanceParsedStates
} from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { Angulartics2 } from 'angulartics2';
import { deepEqual } from 'fast-equals';
import { getOrElse, mapNullable, map as optionMap } from 'fp-ts/es6/Option';
import { pipe } from 'fp-ts/es6/function';
import { findDefaultColumnSelection } from 'libs/flyfreely-table/src/lib/helpers';
import { LocationDialoguesService } from 'libs/locations/src/lib/location-dialogues.service';
import { findRpic } from 'libs/missions/src/lib/helpers';
import { MissionDialoguesService } from 'libs/missions/src/lib/mission-dialogues.service';
import { FormatMissionTypePipe } from 'libs/ui/src/lib/pipes';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
    BehaviorSubject,
    Observable,
    Subject,
    asyncScheduler,
    combineLatest,
    forkJoin,
    merge,
    of,
    scheduled
} from 'rxjs';
import {
    concatAll,
    filter,
    map,
    shareReplay,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    buildDefaultOrganisationFilter,
    buildMissionAuthorityFilterOptions
} from '../helpers';
import { MissionViewMapComponent } from '../mission-view-map/mission-view-map.component';
import { MissionViewService } from '../mission-view.service';
import { MissionViewDataService } from './mission-view-data.service';

export interface WidgetMission extends MissionSummary {
    missionCrewNames?: string[];
    craftNames?: string[];
    combinedStatus?: CombinedMissionStatus;
    convertedDate: moment.Moment;
    convertedRequestTime?: moment.Moment[];
    convertedResolutionTime?: moment.Moment[];
}

interface MissionCriteriaState {
    criteria?: GqlMissionCriteria;
    missionAuthorityFilter?: AuthorityFilter;
    page?: number;
    limit?: number;
    sorting?: MissionSortField;
}

/**
 * This component deals with data in the domain of the table. Where it needs
 * to interface with other systems it is responsible for first converting these
 * values to the domain of that other service.
 */
@Component({
    selector: 'flyfreely-portal-ui-mission-view',
    templateUrl: './mission-view.component.html',
    styleUrls: ['./mission-view.component.scss'],
    providers: [
        MissionViewDataService,
        MissionNoticesDataService,
        SitesDataService,
        OperationsSearchDataService
    ]
})
export class MissionViewComponent implements OnInit, OnDestroy {
    currentOrganisation: PersonsOrganisationDto;

    viewMode: 'MAP' | 'LIST' | 'BOTH' = 'BOTH';

    firstLoad = true;
    newFlightLogs = 0;

    showDummyData$: Observable<boolean>;
    showDummyData: boolean;
    locations: LocationSummary[];

    canFilterByAuthority = false;
    canUseCalendar = true;
    canListMissions = true;
    hasFlightLogging = true;
    canReport = true;
    canAddMission = true;
    canEditMission = true;
    canSeeOwnMissions = false;
    canUseFlightConformance = false;
    canUseNotices = false;
    canUseProjects = false;
    canViewOrganisation = false;

    private preferencesLoaded = false;

    missions: WidgetMission[];

    organisationsMissionFilter: AuthorityFilter;
    missionAuthorities: SimpleAuthorityDto[];

    missionAuthorityFilters$: Observable<
        { name: string; children: AuthorityFilter[] }[]
    >;
    organisationSelector = new FormControl<{
        name: string;
        children: AuthorityFilter[];
    }>(undefined);
    organisationSelectorValue$: Observable<{
        name: string;
        children: AuthorityFilter[];
    }>;
    authoritySelector: FormControl<AuthorityFilter> = new FormControl(
        undefined
    );

    missionOperationTypeFilters: {
        name: string;
        value: number;
    }[];

    projects?: ProjectDto[];

    hasMissions = new BehaviorSubject<boolean>(false);
    orgHasMissions = new BehaviorSubject<boolean | null>(null);
    private organisationUpdated$ = new Subject<void>();

    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig<WidgetMission>;
    tableSearch: any;

    private missionCriteriaSubject = new Subject<
        StateUpdateCommand<MissionCriteriaState>
    >();
    missionCriteria$ = patchableState(this.missionCriteriaSubject, {});

    tableSorting$ = new BehaviorSubject<ColumnSortPreferences | null>(null);

    missionStatusFilters: MissionSummaryDto.Status[];
    projectFilters: number;

    userPreferences: TableSetupUserPreferences;

    private missions$ = new Subject<WidgetMission[]>();
    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    @ViewChild(MissionViewMapComponent)
    private missionMapView: MissionViewMapComponent;

    constructor(
        private organisationAuthorityService: OrganisationAuthorityService,
        private missionDialoguesService: MissionDialoguesService,
        private flightLogsService: FlightLogsService,
        private locationDialoguesService: LocationDialoguesService,
        private workspaceStateService: WorkspaceStateService,
        private preferencesService: PreferencesService,
        private missionService: MissionService,
        private missionViewDataService: MissionViewDataService,
        private missionViewService: MissionViewService,
        private flightLogService: FlightLogsService,
        private toastr: ToastrService,
        private formatAuthority: FormatAuthorityPipe,
        private formatDateTimePipe: FormatDateTimePipe,
        private combinedMissionStatus: FormatMissionStatusPipe,
        private formatColumnArrayPipe: FormatColumnArrayPipe,
        private formatMissionTypePipe: FormatMissionTypePipe,
        private formatPersonPipe: FormatPersonPipe,
        private changeDetector: ChangeDetectorRef,
        private angulartics2: Angulartics2,
        private logging: FlyFreelyLoggingService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private projectsService: ProjectsService
    ) {
        this.showDummyData$ = this.preferencesService.showDummyData$;
    }

    ngOnInit() {
        combineLatest([
            this.workTracker.observable,
            this.missionViewDataService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, loadingTable]) => {
                this.working = working || loadingTable;
                this.missionViewService.setMissionDataLoading(this.working);
            });

        this.showDummyData$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(showDummyData => {
                if (this.showDummyData != showDummyData) {
                    this.showDummyData = showDummyData;
                    this.onTableSearch(this.tableSearch);
                }
            });

        this.missionViewDataService.hasMissions$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(hasMissions => {
                this.hasMissions.next(hasMissions);
                // Populate orgHasMissions after the first fetch using just the org ID.
                // This is to only show the "add missions to get started" banner if the org itself doesn't have missions.
                if (this.orgHasMissions.getValue() == null) {
                    this.orgHasMissions.next(hasMissions);
                }
            });

        combineLatest([
            this.workspaceStateService.currentOrganisation$,
            scheduled(
                [[undefined], this.missionService.change$],
                asyncScheduler
            ).pipe(concatAll()),
            scheduled(
                [[undefined], this.flightLogService.change$],

                asyncScheduler
            ).pipe(concatAll())
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([organisation, _missionChange, _flightLogChange]) => {
                this.projects = undefined;
                if (organisation.type === 'organisation_loaded') {
                    // organisationUpdated$ acts like ngUnsubscribe$ for subscriptions that will need to be restarted on org change
                    this.organisationUpdated$.next();
                    this.currentOrganisation = organisation.organisation;
                    this.missions = [];
                    this.missionCriteriaSubject.next(setState({}));
                    this.refreshPermissions(organisation.organisation);
                    this.refreshTableData();

                    if (this.firstLoad) {
                        this.firstLoad = false;
                        this.setupInitialParameters();
                    } else {
                        this.updateMissionsOnOrganisationChange();
                    }
                } else {
                    console.warn('No organisation loaded');
                    // None visible

                    this.locations = null;
                    this.currentOrganisation = null;
                }
            });

        this.missionViewService.viewMode$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(mode => {
                this.viewMode = mode;
                if (this.tableConfig != null) {
                    this.tableConfig = {
                        ...this.tableConfig,
                        actions: this.computeTableActions()
                    };
                }
            });

        this.missionViewService.showReportsDialogue$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.showMissionReportsDialogue());

        this.missionViewService.showMissionCalendar$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.showMissionCalendar());

        this.missionViewService.showOrganisationLotams$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.showOrganisationLotams());

        this.missionViewService.permissionsUpdated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(updated => {
                if (updated) {
                    this.canFilterByAuthority =
                        this.missionViewService.canFilterByAuthority;
                    this.canUseCalendar =
                        this.missionViewService.canUseCalendar;
                    this.canListMissions =
                        this.missionViewService.canListMissions;
                    this.hasFlightLogging =
                        this.missionViewService.hasFlightLogging;
                    this.canReport = this.missionViewService.canReport;
                    this.canAddMission = this.missionViewService.canAddMission;
                    this.canEditMission =
                        this.missionViewService.canEditMission;
                    this.canSeeOwnMissions =
                        this.missionViewService.canSeeOwnMissions;
                    const previousCanViewOrganisation =
                        this.canViewOrganisation;
                    this.canViewOrganisation =
                        this.missionViewService.canViewOrganisation;
                    // If canViewOrganisation changed and columns are already built,
                    // rebuild them in isolation.
                    if (
                        previousCanViewOrganisation !==
                            this.canViewOrganisation &&
                        this.availableColumns != null
                    ) {
                        this.setupAvailableColumns();
                        this.changeDetector.detectChanges();
                    }
                }
            });

        this.organisationSelectorValue$ = observeFormControl<{
            name: string;
            children: AuthorityFilter[];
        }>(this.organisationSelector);

        this.missionCriteria$.subscribe(criteriaState =>
            this.refreshMissions(criteriaState)
        );

        this.tableSorting$.subscribe(tableSorting => {
            const sortField = validatedSortEntry(
                this.missionViewService.convertSortFieldName(
                    tableSorting?.column ?? null
                ) as MissionSortFieldName,
                tableSorting?.ascending,
                MissionSortFields
            );
            this.missionCriteriaSubject.next(
                patchState({ sorting: sortField })
            );
        });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.missionCriteriaSubject.complete();
        this.tableSorting$.complete();
    }

    /**
     * Resets and reloads authority data when the organisation selector value changes.
     */
    private updateMissionsOnOrganisationChange() {
        this.setupMissionAuthorities();
        this.loadPreferences();
        this.organisationsMissionFilter = buildDefaultOrganisationFilter(
            this.currentOrganisation
        );
        this.missionCriteriaSubject.next(
            patchState({
                missionAuthorityFilter: this.organisationsMissionFilter
            })
        );
    }

    private setupInitialParameters() {
        this.refreshFlightLogs();
        this.missionCriteriaSubject.next(
            patchState({
                missionAuthorityFilter: this.organisationsMissionFilter
            })
        );

        this.watchReturnedMissionValues();
        this.setupMissionAuthorities();
        this.loadPreferences();
        this.organisationsMissionFilter = buildDefaultOrganisationFilter(
            this.currentOrganisation
        );

        combineLatest([
            this.missions$,
            this.missionCriteria$.pipe(
                map(criteria => criteria.missionAuthorityFilter),
                filter(missionAuthorityFilter => missionAuthorityFilter != null)
            )
        ])
            .pipe(
                tap(([missions, missionAuthorityFilter]) => {
                    // If there aren't missions returned it may be because of filters, check if the org has any missions at all
                    // If there is an orOrganisationId in the authority filters, use the filter IDs instead to find missions from shared authorities.
                    if (missions.length === 0) {
                        const checkCurrentTotal = () => {
                            this.missionViewDataService.checkMissionTotals(
                                missionAuthorityFilter.orOrganisationId ??
                                    missionAuthorityFilter.organisationId ??
                                    this.currentOrganisation.id,
                                missionAuthorityFilter.orOrganisationId != null
                                    ? this.missionAuthorities.map(a => a.id)
                                    : undefined,
                                missionAuthorityFilter.orOrganisationId
                            );
                        };

                        if (
                            this.orgHasMissions.getValue() == null &&
                            missionAuthorityFilter.orOrganisationId != null
                        ) {
                            // If the mission total for the org itself without filters hasn't run yet, and
                            // the filters on the widget are using orOrganisation, run the check for the org itself first,
                            // then run it for the current widget settings.
                            this.missionViewDataService
                                .checkMissionTotals(this.currentOrganisation.id)
                                .add(() => checkCurrentTotal());
                        } else {
                            checkCurrentTotal();
                        }
                    }
                })
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([missions]) => {
                this.missions = missions;
            });
    }

    private refreshPermissions(organisation: PersonsOrganisationDto) {
        this.missionViewService.refreshPermissions(organisation);

        this.canUseFlightConformance = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_FLIGHT_CONFORMANCE
        );

        this.canUseNotices = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_LOTAM
        );

        this.canUseProjects = hasFeatureFlag(
            this.currentOrganisation,
            FEATURE_PROJECTS
        );
    }

    private updateMissionsList(missions: MissionSummary[]) {
        this.missions$.next(
            missions.map(m => ({
                ...m,
                missionCrewNames: this.missionViewService.findCrew(
                    m.missionCrew
                ),
                rpic: findRpic(m.missionCrew),
                combinedStatus: this.missionService.getCombinedStatus(m),
                convertedDate: moment(m.missionDate).tz(m.timeZone)
            }))
        );
    }

    private refreshTableData() {
        if (this.canUseProjects && this.canViewOrganisation) {
            this.projectsService
                .find(this.currentOrganisation.id)
                .subscribe({
                    next: (projects: ProjectDto[]) => {
                        this.projects = projects;
                        this.setupTable();
                    },
                    error: (error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error while setting up project filters: ${error.message}`
                        );
                        this.setupTable();
                    }
                })
                .add(this.workTracker.createTracker());
        } else {
            this.setupTable();
        }
    }

    private refreshMissions({
        criteria,
        page,
        limit,
        missionAuthorityFilter,
        sorting
    }: MissionCriteriaState) {
        if (
            criteria == null ||
            page == null ||
            limit == null ||
            missionAuthorityFilter == null
        ) {
            return;
        }

        if (
            this.currentOrganisation == null ||
            (!this.canListMissions && !this.canSeeOwnMissions)
        ) {
            return;
        }

        const missionSearchCriteria = {
            ...criteria,
            organisationId: missionAuthorityFilter?.organisationId,
            orOrganisationId: missionAuthorityFilter?.orOrganisationId,
            authorityIds: missionAuthorityFilter?.authorityIds
        };

        this.missionViewDataService.findMissions(
            missionSearchCriteria,
            page,
            limit,
            sorting ?? null
        );
    }

    refreshCalendarMissions(startTime: string, endTime: string) {
        if (
            this.currentOrganisation == null ||
            (!this.canListMissions && !this.canSeeOwnMissions)
        ) {
            return;
        }

        // Use the current criteria state (which reflects the active table filters/authority filter)
        // and override the time range with the calendar's requested window.
        this.missionCriteria$
            .pipe(take(1))
            .subscribe(({ criteria, missionAuthorityFilter, sorting }) => {
                if (missionAuthorityFilter == null) {
                    return;
                }

                const calendarCriteria: GqlMissionCriteria = {
                    ...(criteria ?? {}),
                    organisationId: missionAuthorityFilter.organisationId,
                    orOrganisationId: missionAuthorityFilter.orOrganisationId,
                    authorityIds: missionAuthorityFilter.authorityIds,
                    startTime,
                    endTime
                };

                this.missionViewDataService.findCalendarMissions(
                    calendarCriteria
                );
            });
    }

    private setupMissionAuthorities() {
        if (!this.canViewOrganisation) {
            this.missionAuthorityFilters$ = of([]);
            return;
        }
        const today = moment().format('YYYY-MM-DD');

        this.missionAuthorityFilters$ = combineLatest([
            this.organisationAuthorityService.findAuthorities(
                this.currentOrganisation.id,
                this.currentOrganisation.id
            ),
            this.organisationAuthorityService.findSharedAuthorities(
                this.currentOrganisation.id,
                today
            )
        ]).pipe(
            map(([authorities, sharedAuthorityGroups]) => {
                const { filterOptions, missionAuthorities } =
                    buildMissionAuthorityFilterOptions(
                        this.currentOrganisation,
                        authorities,
                        sharedAuthorityGroups,
                        a => this.formatAuthority.transform(a)
                    );
                this.missionAuthorities = missionAuthorities;
                this.organisationSelector.patchValue(filterOptions[0]);
                this.authoritySelector.patchValue(filterOptions[0].children[0]);
                return filterOptions;
            }),
            shareReplay(),
            // unsubscribe on either component destroy or when an org changes since this function will be called again.
            takeUntil(merge(this.ngUnsubscribe$, this.organisationUpdated$))
        );
    }

    updateMissionAuthorityFilter(missionAuthorityFilter: AuthorityFilter) {
        if (this.currentOrganisation == null || !this.preferencesLoaded) {
            return;
        }

        this.preferencesService
            .updatePreferences(
                'missionListWidgetAuthorityFilter',
                this.currentOrganisation.id,
                missionAuthorityFilter
            )
            .pipe(
                // unsubscribe on either component destroy or when an org changes since this function will be called again.
                takeUntil(merge(this.ngUnsubscribe$, this.organisationUpdated$))
            )
            .subscribe({
                next: DO_NOTHING,
                error: error =>
                    this.logging.error(
                        error,
                        'Updating mission authority preferences'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    private watchReturnedMissionValues() {
        combineLatest([
            this.missionViewDataService.missions$,
            this.missionViewDataService.currentPage$,
            this.missionViewDataService.totalItems$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([missions, currentPage, totalItems]) => {
                this.updateMissionsList(missions);
                if (this.tableConfig != null) {
                    const currentConfig = this.tableConfig;
                    this.tableConfig = {
                        ...currentConfig,
                        currentPage: currentPage,
                        totalItems: totalItems
                    };
                }
            });
    }

    private refreshFlightLogs() {
        if (!this.canListMissions) {
            return;
        }
        this.flightLogsService
            .findCounts(this.currentOrganisation.id)
            .pipe(
                // unsubscribe on either component destroy or when an org changes since this function will be called again.
                takeUntil(merge(this.ngUnsubscribe$, this.organisationUpdated$))
            )
            .subscribe({
                next: counts => {
                    this.newFlightLogs =
                        counts.byStatus[
                            FlightLogFileDto.AssignmentStatus.UNASSIGNED
                        ];
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(error, 'Refreshing flight log counts')
            })
            .add(this.workTracker.createTracker());
    }

    private refreshMissionTypeFilter() {
        if (!this.canViewOrganisation) {
            return;
        }
        this.missionService
            .findMissionTypes(this.currentOrganisation.id)
            .pipe(
                // unsubscribe on either component destroy or when an org changes since this function will be called again.
                takeUntil(merge(this.ngUnsubscribe$, this.organisationUpdated$))
            )
            .subscribe({
                next: missionTypes => {
                    const missionTypeOptions = missionTypes.map(t => ({
                        value: t.id,
                        name: t.name
                    }));
                    const col = this.availableColumns.find(
                        col => col.value === 'missionType.name'
                    );
                    col.searchOptions = missionTypeOptions;
                    col.searchable = 'selection';
                    this.missionOperationTypeFilters = missionTypeOptions;
                },
                error: error => {
                    const col = this.availableColumns.find(
                        col => col.value === 'missionType.name'
                    );
                    col.searchable = false;
                    col.searchOptions = [];
                    this.logging.error(error, 'Refreshing mission types');
                }
            })
            .add(this.workTracker.createTracker());
    }

    private loadPreferences() {
        if (!this.currentOrganisation) {
            return;
        }

        const refreshAuthorityFilter = forkJoin([
            this.missionAuthorityFilters$,
            this.preferencesService.findPreferencesAsOption<AuthorityFilter>(
                'missionListWidgetAuthorityFilter',
                this.currentOrganisation.id
            )
        ]).pipe(
            tap(([filter, preferences]) => {
                const allAuthorityFilters =
                    filter?.reduce(
                        (acc, v) => [...acc, ...v.children],
                        [] as AuthorityFilter[]
                    ) ?? [];
                const missionAuthorityFilter = pipe(
                    preferences,
                    mapNullable<AuthorityFilter, AuthorityFilter>(selected =>
                        selected?.filterType === 'ALL_AUTHORITIES'
                            ? allAuthorityFilters?.find(
                                  f =>
                                      f.filterType === 'ALL_AUTHORITIES' &&
                                      f.organisationId ===
                                          selected.organisationId &&
                                      f.orOrganisationId ===
                                          selected.orOrganisationId
                              )
                            : allAuthorityFilters.find(f =>
                                  deepEqual(f, selected)
                              )
                    ),
                    getOrElse<AuthorityFilter>(
                        () => this.organisationsMissionFilter ?? null
                    )
                );

                // Find the appropriate mission authority filter objects and assign them in order so the required values are available
                const selectedOrg = filter.find(
                    o =>
                        o.children.filter(m => m == missionAuthorityFilter)
                            .length > 0
                );
                this.organisationSelector.patchValue(selectedOrg);
                this.authoritySelector.patchValue(missionAuthorityFilter);
                this.missionCriteriaSubject.next(
                    patchState({ missionAuthorityFilter })
                );
                this.setupOrganisationAuthorityListeners();
            })
        );

        const refreshUserPreferences = () => {
            this.preferencesService
                .findPreferences(
                    'missionListWidgetColumns',
                    this.currentOrganisation.id
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(pref => {
                    this.userPreferences = {
                        selectedColumns: pref,
                        columnSorting: {
                            column: 'missionDate',
                            ascending: false
                        },
                        tableSearch: this.userPreferences.tableSearch
                    };
                    this.setupTableDefaults();
                });
        };

        return forkJoin([
            this.preferencesService.findPreferencesAsOption<TableSetupUserPreferences>(
                'missionListWidgetTable',
                this.currentOrganisation.id
            ),
            refreshAuthorityFilter
        ])
            .pipe(
                // unsubscribe on either component destroy or when an org changes since this function will be called again.
                takeUntil(merge(this.ngUnsubscribe$, this.organisationUpdated$))
            )
            .subscribe({
                next: ([preferences, _]) => {
                    if (preferences == null) {
                        refreshUserPreferences();
                    } else {
                        this.userPreferences =
                            getOrElse<TableSetupUserPreferences>(() => ({
                                selectedColumns: findDefaultColumnSelection(
                                    this.availableColumns
                                ),
                                columnSorting: null,
                                tableSearch: {
                                    combinedStatus:
                                        'DRAFT|SUBMITTED|READY_TO_FLY|PREPARED|FLYING|ON_SITE|DONE_FLYING|COMPLETED|READY_FOR_FINALISATION'
                                },
                                itemLimit: this.tableConfig.limit
                            }))(
                                pipe(
                                    preferences,
                                    optionMap(
                                        this.missionViewService.fixFilter(
                                            this.availableColumns
                                        )
                                    )
                                )
                            );

                        this.setupTableDefaults();
                    }
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        'Loading mission view preferences'
                    ),
                complete: () => {
                    this.onTableSearch(this.tableSearch);
                    this.preferencesLoaded = true;
                }
            })
            .add(this.workTracker.createTracker());
    }

    updateUserPreferences() {
        if (this.currentOrganisation == null || !this.preferencesLoaded) {
            return;
        }

        this.preferencesService
            .updatePreferences(
                'missionListWidgetTable',
                this.currentOrganisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    private setupTableDefaults() {
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.tableSorting$.next(this.userPreferences.columnSorting);
        // Don't apply saved search filters when the user can't search columns
        this.tableSearch = this.canViewOrganisation
            ? this.userPreferences.tableSearch
            : null;
        this.tableConfig.limit =
            this.userPreferences.itemLimit != null
                ? this.userPreferences.itemLimit
                : this.tableConfig.limit;
        this.changeDetector.detectChanges();
    }

    /**
     * The organisation authority control listeners should only be setup after
     * preferences have loaded to prevent invalid data being persisted
     */
    private setupOrganisationAuthorityListeners() {
        // Setup component cascade
        this.organisationSelectorValue$
            .pipe(
                // unsubscribe on either component destroy or when an org changes since this function will be called again.
                takeUntil(merge(this.ngUnsubscribe$, this.organisationUpdated$))
            )
            .subscribe(selectedOrganisation => {
                if (!!selectedOrganisation && !!selectedOrganisation.children) {
                    this.authoritySelector.setValue(
                        selectedOrganisation.children[0]
                    );
                }
            });

        observeFormControl<AuthorityFilter>(this.authoritySelector)
            .pipe(
                // unsubscribe on either component destroy or when an org changes since this function will be called again.
                takeUntil(merge(this.ngUnsubscribe$, this.organisationUpdated$))
            )
            .subscribe(authority => {
                if (!authority) {
                    return;
                }
                this.missionCriteriaSubject.next(
                    patchState({ missionAuthorityFilter: authority })
                );
                this.updateMissionAuthorityFilter(authority);
            });

        // Check if filters should be visible and reset if necessary
        if (!this.canFilterByAuthority || !this.canListMissions) {
            this.resetAuthorityFilters();
        }
    }

    /**
     * Resets authority filter settings when user doesn't have required permissions
     */
    private resetAuthorityFilters() {
        if (this.currentOrganisation == null) {
            return;
        }

        // Reset to default organization filter
        this.organisationsMissionFilter = buildDefaultOrganisationFilter(
            this.currentOrganisation
        );
        this.missionCriteriaSubject.next(
            patchState({
                missionAuthorityFilter: this.organisationsMissionFilter
            })
        );

        // Clear the form controls
        this.organisationSelector.reset();
        this.authoritySelector.reset();

        // Update preferences to clear saved filter
        this.preferencesService
            .updatePreferences(
                'missionListWidgetAuthorityFilter',
                this.currentOrganisation.id,
                this.organisationsMissionFilter
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: DO_NOTHING,
                error: error =>
                    this.logging.error(
                        error,
                        'Resetting mission authority preferences'
                    )
            });
    }

    /**
     * Builds the availableColumns array based on current state flags
     * (canViewOrganisation, canUseProjects, canUseFlightConformance, projects).
     * Can be called in isolation when only the column definitions need to be
     * refreshed (e.g. after a permission change) without rebuilding the full
     * table configuration.
     */
    private setupAvailableColumns() {
        const statusFilters = this.missionService.getCombinedStatusFilters();

        this.availableColumns = [
            {
                value: 'organisationName',
                name: 'Organisation',
                searchable: false,
                selectable: true,
                defaultSelection: false
            },
            {
                value: 'name',
                name: 'Name',
                searchable: this.canViewOrganisation,
                selectable: false,
                defaultSelection: true
            },
            ...(this.canUseProjects &&
            this.projects != null &&
            this.projects.length > 0
                ? [
                      {
                          value: 'project',
                          name: 'Project',
                          searchable: this.canViewOrganisation,
                          selectable: true,
                          defaultSelection: true,
                          searchOptions: this.projects.map(p => ({
                              name: p.name,
                              value: p.id
                          })),
                          formatterFunction: (p: ProjectDto) =>
                              p != null ? p.name : ''
                      }
                  ]
                : []),
            {
                value: 'uid',
                name: 'UID',
                searchable: this.canViewOrganisation,
                selectable: true,
                defaultSelection: false
            },
            {
                value: 'convertedDate',
                key: 'missionDate',
                name: 'Date/Time',
                searchable: this.canViewOrganisation ? 'daterange' : false,
                selectable: true,
                defaultSelection: true,
                formatterFunction: t => this.formatDateTimePipe.transform(t)
                // searchFunction: () => () => true
            },
            {
                value: 'locationName',
                name: 'Location',
                searchable: this.canViewOrganisation,
                selectable: true,
                defaultSelection: true
            },
            {
                value: 'combinedStatus',
                name: 'Status',
                searchable: this.canViewOrganisation,
                selectable: true,
                sortable: false,
                defaultSelection: true,
                searchOptions: statusFilters,
                formatterFunction: s => this.combinedMissionStatus.transform(s)
            },
            {
                value: 'primaryMissionApproval.requestTime',
                key: 'convertedRequestTime',
                name: 'Request Date',
                searchable: this.canViewOrganisation ? 'daterange' : false,
                selectable: true,
                sortable: false,
                defaultSelection: false,
                formatterFunction: t => this.formatDateTimePipe.transform(t)
                // searchFunction: () => () => true
            },
            {
                value: 'primaryMissionApproval.resolutionTime',
                key: 'convertedResolutionTime',
                name: 'Approval Date',
                searchable: this.canViewOrganisation ? 'daterange' : false,
                selectable: true,
                sortable: false,
                defaultSelection: false,
                formatterFunction: t => this.formatDateTimePipe.transform(t)

                // searchFunction: () => () => true
            },
            {
                value: 'type',
                name: 'Mission Type',
                searchable: this.canViewOrganisation ? 'selection' : false,
                selectable: true,
                sortable: false,
                defaultSelection: true,
                searchOptions: MISSION_TYPES,
                formatterFunction: t => this.formatMissionTypePipe.transform(t)
            },
            {
                value: 'missionType.name',
                key: 'missionType',
                name: 'Operation Type',
                searchable: false,
                selectable: true,
                sortable: false,
                defaultSelection: true,
                searchOptions: []
            },
            {
                value: 'rpic',
                name: 'RPIC',
                searchable: this.canViewOrganisation ? 'text' : false,
                selectable: false,
                sortable: true,
                defaultSelection: true,
                formatterFunction: p => this.formatPersonPipe.transform(p)
            },
            {
                value: 'missionCrewNames',
                name: 'Crew',
                searchable: this.canViewOrganisation ? 'text' : false,
                selectable: true,
                sortable: false,
                defaultSelection: true,
                formatterFunction: a =>
                    this.formatColumnArrayPipe.transform(a, 2)
            },
            {
                value: 'craftNicknames',
                name: 'RPA',
                searchable: this.canViewOrganisation,
                selectable: true,
                sortable: false,
                defaultSelection: true,
                formatterFunction: a =>
                    this.formatColumnArrayPipe.transform(a, 1)
            },
            {
                value: 'missionWorkflowVersion.name',
                key: 'missionWorkflowVersion',
                name: 'Workflow',
                searchable: this.canViewOrganisation,
                selectable: true,
                defaultSelection: true
            },
            ...(this.canUseFlightConformance
                ? [
                      {
                          value: 'flightConformanceResultList',
                          name: 'Conformance',
                          searchable: this.canViewOrganisation,
                          selectable: true,
                          sortable: false,
                          defaultSelection: true,
                          searchOptions: flightConformanceParsedStates
                      }
                  ]
                : [])
        ];
    }

    private setupTable() {
        this.setupAvailableColumns();
        this.refreshMissionTypeFilter();

        this.tableConfig = {
            currentPage: 0,
            limit: 10,
            serverPagination: true,
            actions: this.computeTableActions(),
            limitSelection: [10, 25, 50, 100]
        };

        // this.tableSorting = this.tableSorting ?? null;

        this.tableSearch = this.tableSearch ?? null;

        this.selectedColumns = this.selectedColumns ?? null;

        this.missionCriteriaSubject.next(
            patchState({
                page: this.tableConfig.currentPage,
                limit: this.tableConfig.limit,
                sorting: null
            })
        );

        // this.refreshMissions();
        this.refreshFlightLogs();
    }

    private computeTableActions(): TableAction<WidgetMission>[] {
        return [
            ...(this.canAddMission
                ? [
                      {
                          action: (item: WidgetMission) =>
                              this.cloneMission(item),
                          icon: 'fal fa-clone',
                          tooltip: 'Clone Mission'
                      }
                  ]
                : []),
            ...(this.viewMode === 'BOTH'
                ? [
                      {
                          action: (item: WidgetMission) =>
                              this.zoomToMission(item),
                          icon: 'fal fa-location-crosshairs',
                          tooltip: 'Zoom to Mission',
                          disabled: m => m?.location?.boundary == null
                      }
                  ]
                : [])
        ];
    }

    showMission(mission: MissionSummary) {
        this.router.navigate(['mission', mission?.id], {
            queryParamsHandling: 'preserve',
            relativeTo: this.activatedRoute
        });
    }

    cloneMission(mission: WidgetMission) {
        this.angulartics2.eventTrack.next({
            action: 'dashboard-mission-clone'
        });
        this.missionService
            .findMission(mission.id, mission.organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: loadedMission =>
                    this.missionDialoguesService.showMissionEditor(
                        this.missionService.cloneMission(loadedMission),
                        this.currentOrganisation,
                        mission.id
                    ),
                error: (error: FlyFreelyError) =>
                    this.toastr.error(
                        `Error while fetching mission to clone: ${error.message}`
                    )
            });
    }

    showMissionReportsDialogue() {
        combineLatest([
            this.missionCriteria$.pipe(take(1)),
            this.missionAuthorityFilters$.pipe(take(1))
        ])
            .pipe(take(1))
            .subscribe(([criteriaState, missionAuthorityFilters]) => {
                const missionAuthorityFilter =
                    criteriaState.missionAuthorityFilter;
                if (missionAuthorityFilter == null) {
                    return;
                }

                const authorityIds =
                    missionAuthorityFilter.authorityIds == null ||
                    missionAuthorityFilter.authorityIds.length === 0
                        ? this.missionAuthorities.map(a => a.id)
                        : missionAuthorityFilter.authorityIds;

                this.missionDialoguesService.showMissionReportsDialogue(
                    missionAuthorityFilter.organisationId,
                    missionAuthorityFilter.orOrganisationId,
                    authorityIds,
                    this.canFilterByAuthority,
                    missionAuthorityFilters,
                    missionAuthorityFilter
                );
            });
    }

    showMissionCalendar() {
        const dialogue =
            this.missionDialoguesService.showMissionCalendarDialogue(
                this.missionViewDataService.calendarMissions$
            );

        dialogue.content.showMission
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(dialogue.onHidden))
            .subscribe(mission => this.showMission(mission));

        dialogue.content.updateMissions
            .pipe(takeUntil(this.ngUnsubscribe$), takeUntil(dialogue.onHidden))
            .subscribe(({ startTime, endTime }) =>
                this.refreshCalendarMissions(startTime, endTime)
            );
        dialogue.content.refreshMissions();
    }

    showOrganisationLotams() {
        this.router.navigate(['lotams'], {
            queryParamsHandling: 'preserve',
            relativeTo: this.activatedRoute
        });
    }

    showAddLocation() {
        this.angulartics2.eventTrack.next({
            action: 'dashboard-airspace-check',
            properties: {
                category: `airspace-check`
            }
        });
        this.locationDialoguesService.newLocation(
            this.currentOrganisation.id,
            LocationDto.Type.MISSION,
            {
                canCreateMission: true
            }
        );
    }

    onMissionSearchUpdated(updatedTimes: {
        startTime: string;
        endTime: string;
    }) {
        if (this.tableSearch != null) {
            this.tableSearch.missionDate = [
                moment(updatedTimes.startTime).tz(defaultTimezone).toDate(),
                moment(updatedTimes.endTime).tz(defaultTimezone).toDate()
            ];
            this.onTableSearch(this.tableSearch);
        }
    }

    onTableSearch(search: TableSearchValues) {
        this.tableSearch = search;

        const criteria: DeepPartial<GqlMissionCriteria> =
            search == null || Object.keys(search).length === 0
                ? {}
                : Object.keys(search)
                      .filter(
                          k =>
                              this.availableColumns.find(
                                  c => c.key === k || c.value === k
                              )?.searchable
                      )
                      .reduce(
                          (acc, k) => ({
                              ...acc,
                              ...this.missionViewService.toSearchEntryStateUpdate(
                                  k,
                                  search[k]
                              )
                          }),
                          {}
                      );

        criteria.isDummy = !this.showDummyData ? false : null;

        this.userPreferences = {
            ...this.userPreferences,
            tableSearch: search
        };
        if (this.tableConfig != null) {
            this.tableConfig.currentPage = 0;
        }
        // This is needed because the static table uses onPush change detection
        // Whenever a query repeats a previous output there is a chance the table won't update without explicit change detection
        this.changeDetector.detectChanges();
        // this.refreshMissions();
        this.missionCriteriaSubject.next(
            patchState({
                criteria,
                page: this.tableConfig?.currentPage,
                limit: this.tableConfig?.limit
            })
        );

        this.updateUserPreferences();
    }

    onTablePageChanged(page: number) {
        this.tableConfig.currentPage = page;
        // this.refreshMissions();
        this.missionCriteriaSubject.next(
            patchState<MissionCriteriaState>({ page })
        );
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;

        if (
            !this.selectedColumns.includes(
                this.userPreferences.columnSorting?.column
            )
        ) {
            this.tableSorting$.next(null);
        }
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.tableSorting$.getValue() ?? undefined
        };
        // this.refreshMissions();
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.tableSorting$.next(sorting);

        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.tableConfig.currentPage = 0;
        this.missionCriteriaSubject.next(
            patchState<MissionCriteriaState>({ page: 0 })
        );
        // this.refreshMissions();
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.tableConfig.currentPage = 0;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.missionCriteriaSubject.next(
            patchState<MissionCriteriaState>({ page: 0, limit: limit })
        );
        // this.refreshMissions();
        this.updateUserPreferences();
    }

    parseConformanceStatus(conformances: FlightConformanceResultDto[]) {
        if (conformances == null || conformances.length === 0) {
            return;
        }
        return findMostPressingConformance(conformances);
    }

    zoomToMission(mission: WidgetMission) {
        if (mission.location?.boundary == null || this.missionMapView == null) {
            return;
        }

        this.missionMapView.zoomToGeometry(mission.location.boundary);
    }
}
